import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, rgba(10, 10, 10, 0.95) 0%, rgba(15, 15, 15, 0.95) 100%);
  backdrop-filter: blur(20px);
  border-top: 2px solid rgba(34, 197, 94, 0.3);
  margin-top: 60px;
  padding: 40px 20px 30px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #22c55e, transparent);
    animation: topGlow 3s ease-in-out infinite;
  }
  
  @keyframes topGlow {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  align-items: start;
`;

const FooterSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionTitle = styled.h3`
  color: #22c55e;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 0 10px rgba(34, 197, 94, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 30px;
    height: 2px;
    background: linear-gradient(90deg, #22c55e, transparent);
  }
`;

const FooterText = styled.p`
  color: #a0a0a0;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  transition: color 0.3s ease;
  
  &:hover {
    color: #ffffff;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.05));
  border: 2px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
  color: #22c55e;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  will-change: transform, box-shadow;
  transform: translateZ(0);
  
  &:hover {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1));
    border-color: rgba(34, 197, 94, 0.6);
    color: #ffffff;
    transform: translateY(-3px) translateZ(0);
    box-shadow: 0 8px 25px rgba(34, 197, 94, 0.3);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #a0a0a0;
  font-size: 0.9rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: #ffffff;
  }
`;

const ContactIcon = styled.span`
  color: #22c55e;
  font-size: 1rem;
  width: 20px;
  text-align: center;
`;

const Copyright = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding-top: 30px;
  border-top: 1px solid rgba(34, 197, 94, 0.2);
  margin-top: 20px;
  color: #666;
  font-size: 0.85rem;
  
  a {
    color: #22c55e;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: #ffffff;
    }
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
`;

const FooterLogo = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05) translateZ(0);
    box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
  }
`;

const LogoText = styled.div`
  color: #22c55e;
  font-size: 1.5rem;
  font-weight: 800;
  text-shadow: 0 0 10px rgba(34, 197, 94, 0.3);
`;

function Footer() {
  return (
    <FooterContainer>
      <FooterContent>
        {/* 品牌信息 */}
        <FooterSection
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <LogoSection>
            <FooterLogo src="/logo.png" alt="XWF Logo" />
            <LogoText>XWF</LogoText>
          </LogoSection>
          <FooterText>
            XWF Token Generator - 创造你的专属头像!
            体验最酷炫的头像编辑工具，打造独一无二的数字身份。
          </FooterText>
        </FooterSection>

        {/* 快速链接 */}
        <FooterSection
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <SectionTitle>快速链接</SectionTitle>
          <FooterText>• 头像编辑器</FooterText>
          <FooterText>• 贴纸工具</FooterText>
          <FooterText>• 文字编辑</FooterText>
          <FooterText>• 保存分享</FooterText>
        </FooterSection>

        {/* 社交媒体 */}
        <FooterSection
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <SectionTitle>关注我们</SectionTitle>
          <SocialLinks>
            <SocialLink
              href="https://twitter.com/xwf_token"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              🐦
            </SocialLink>
            <SocialLink
              href="https://t.me/xwf_community"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              📱
            </SocialLink>
            <SocialLink
              href="https://discord.gg/xwf"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              💬
            </SocialLink>
            <SocialLink
              href="https://github.com/xwf-token"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              🔧
            </SocialLink>
          </SocialLinks>
        </FooterSection>

        {/* 联系信息 */}
        <FooterSection
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <SectionTitle>联系我们</SectionTitle>
          <ContactInfo>
            <ContactItem>
              <ContactIcon>📧</ContactIcon>
              <span>contact@xwf-token.com</span>
            </ContactItem>
            <ContactItem>
              <ContactIcon>🌐</ContactIcon>
              <span>www.xwf-token.com</span>
            </ContactItem>
            <ContactItem>
              <ContactIcon>📍</ContactIcon>
              <span>OKX Xlayer Chain</span>
            </ContactItem>
            <ContactItem>
              <ContactIcon>🔗</ContactIcon>
              <span>0x7ea7bae59b389aad6fdd8d7ee48e114e35c1114a</span>
            </ContactItem>
          </ContactInfo>
        </FooterSection>

        {/* 版权信息 */}
        <Copyright>
          <p>
            © 2024 XWF Token. All rights reserved. | 
            <a href="/privacy">隐私政策</a> | 
            <a href="/terms">服务条款</a> | 
            <a href="/disclaimer">免责声明</a>
          </p>
          <p style={{ marginTop: '8px', fontSize: '0.8rem' }}>
            Powered by React & Fabric.js | Made with ❤️ for the XWF Community
          </p>
        </Copyright>
      </FooterContent>
    </FooterContainer>
  );
}

export default Footer;
